// src/stores/emailStore.js
import { defineStore } from "pinia";
import EmailService from "@/services/emailService";
import { toast } from "@/utils/toast";

export const useEmailStore = defineStore("email", {
  state: () => ({
    // list
    emails: [],
    loadingList: false,
    error: null,

    // folders + filters
    activeFolder: "inbox",
    activeLabelId: null,
    search: "",
    selectedIds: [],

    // pagination
    meta: {
      total: 0,
      per_page: 25,
      current_page: 1,
      last_page: 1,
    },

    // counters
    counts: {
      inbox: { total: 0, unread: 0 },
      sent: { total: 0, unread: 0 },
      drafts: { total: 0, unread: 0 },
      trash: { total: 0, unread: 0 },
      spam: { total: 0, unread: 0 },
      starred: { total: 0, unread: 0 }, // UI only for now
    },

    // labels
    labels: [],
    loadingLabels: false,

    // email detail
    activeEmail: null,
    loadingEmail: false,

    // Step 7 flags
    sending: false,
    draftSaving: false,
    draftId: null,
    lastDraftSavedAt: null,

    // Step 8 attachments (compose/reply temp buffer)
    attachmentsUploading: false,
    attachments: [],
  }),

  getters: {
    activeFolderLabel(state) {
      const m = {
        inbox: "Inbox",
        sent: "Sent",
        drafts: "Drafts",
        trash: "Deleted",
        spam: "Spam",
        starred: "Starred",
      };
      return m[state.activeFolder] || "Inbox";
    },
  },

  actions: {
    // ----------------------------
    // UI filters
    // ----------------------------
    setFolder(folder) {
      this.activeFolder = folder;
      this.meta.current_page = 1;
      this.selectedIds = [];
      this.activeEmail = null;
    },

    setLabel(id) {
      this.activeLabelId = id || null;
      this.meta.current_page = 1;
    },

    setSearch(q) {
      this.search = q || "";
      this.meta.current_page = 1;
    },

    setPage(p) {
      this.meta.current_page = Math.max(1, Number(p || 1));
    },

    toggleSelect(id) {
      const i = this.selectedIds.indexOf(id);
      if (i === -1) this.selectedIds.push(id);
      else this.selectedIds.splice(i, 1);
    },

    // ----------------------------
    // Labels
    // ----------------------------
    async fetchLabels() {
      this.loadingLabels = true;
      try {
        const res = await EmailService.getLabels();
        const rows = res.data?.data ?? res.data ?? [];
        this.labels = Array.isArray(rows) ? rows : [];
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to fetch labels", "error");
      } finally {
        this.loadingLabels = false;
      }
    },

    async createLabel(payload) {
      try {
        const res = await EmailService.createLabel(payload);
        toast("Label created");
        await this.fetchLabels();
        return res.data;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to create label", "error");
        throw e;
      }
    },

    // ----------------------------
    // Emails list
    // ----------------------------
    async fetchEmails() {
      // starred is UI-only for now
      if (this.activeFolder === "starred") {
        this.emails = [];
        this.meta = { ...this.meta, total: 0, current_page: 1, last_page: 1 };
        return;
      }

      this.loadingList = true;
      this.error = null;

      try {
        const params = {
          folder: this.activeFolder,
          label_id: this.activeLabelId,
          q: this.search,
          page: this.meta.current_page,
          per_page: this.meta.per_page,
        };

        const res = await EmailService.listEmails(params);

        const rows = res.data?.data ?? [];
        const meta = res.data?.meta ?? {};
        const counts = res.data?.counts ?? null;

        this.emails = Array.isArray(rows) ? rows : [];

        this.meta = {
          total: Number(meta.total ?? 0),
          per_page: Number(meta.per_page ?? this.meta.per_page ?? 25),
          current_page: Number(meta.current_page ?? 1),
          last_page: Number(meta.last_page ?? 1),
        };

        if (counts) {
          this.counts = { ...this.counts, ...counts };
        } else {
          const total = this.meta.total || this.emails.length;
          const unread = this.emails.filter((x) => !x.is_read).length;
          this.counts = {
            ...this.counts,
            [this.activeFolder]: { total, unread },
          };
        }

        return res.data;
      } catch (e) {
        this.error = e?.response?.data?.message || "Failed to fetch emails";
        toast(this.error, "error");
        throw e;
      } finally {
        this.loadingList = false;
      }
    },

    // ----------------------------
    // Email detail
    // ----------------------------
    async fetchEmail(id, { markRead = true } = {}) {
      this.loadingEmail = true;
      try {
        const res = await EmailService.getEmail(id);
        const email = res.data?.data || res.data;
        this.activeEmail = email;

        if (markRead && email?.is_read === false) {
          await this.markRead(id, true);
          if (this.activeEmail?.id === id) this.activeEmail.is_read = true;
        }

        return email;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to load email", "error");
        throw e;
      } finally {
        this.loadingEmail = false;
      }
    },

    async markRead(id, read = true) {
      try {
        await EmailService.setRead(id, !!read);

        const row = this.emails.find((x) => x.id === id);
        if (row) row.is_read = !!read;

        if (this.activeEmail?.id === id) this.activeEmail.is_read = !!read;

        // refresh counts in sidebar
        await this.fetchEmails();
      } catch (e) {
        toast(
          e?.response?.data?.message || "Failed to update read status",
          "error"
        );
      }
    },

    async moveEmail(id, folder) {
      try {
        await EmailService.moveEmail(id, folder);
        toast("Email moved");
        await this.fetchEmails();
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to move email", "error");
      }
    },

    async deleteEmail(id) {
      try {
        await EmailService.deleteEmail(id);
        toast("Email deleted");
        await this.fetchEmails();
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to delete email", "error");
      }
    },

    // ----------------------------
    // Step 7: send / reply / drafts
    // ----------------------------
    async sendEmail(payload) {
      this.sending = true;
      try {
        const res = await EmailService.send(payload);
        toast(res.data?.message || "Email sent");
        await this.fetchEmails();
        return res.data?.data || res.data;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to send email", "error");
        throw e;
      } finally {
        this.sending = false;
      }
    },

    async replyToEmail(emailId, payload) {
      this.sending = true;
      try {
        const res = await EmailService.reply(emailId, payload);
        toast(res.data?.message || "Reply sent");

        // backend returns updated email thread
        if (this.activeEmail?.id === emailId) {
          this.activeEmail = res.data?.data || res.data;
        }
        return res.data?.data || res.data;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to send reply", "error");
        throw e;
      } finally {
        this.sending = false;
      }
    },

    async createDraft(payload) {
      this.draftSaving = true;
      try {
        const res = await EmailService.createDraft(payload);
        const data = res.data?.data || res.data;
        this.draftId = data?.id || null;
        this.lastDraftSavedAt = new Date();
        toast("Draft saved");
        return data;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to save draft", "error");
        throw e;
      } finally {
        this.draftSaving = false;
      }
    },

    async updateDraft(draftId, payload) {
      this.draftSaving = true;
      try {
        const res = await EmailService.updateDraft(draftId, payload);
        this.lastDraftSavedAt = new Date();
        return res.data?.data || res.data;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to update draft", "error");
        throw e;
      } finally {
        this.draftSaving = false;
      }
    },

    async sendDraft(draftId, payload) {
      this.sending = true;
      try {
        const res = await EmailService.sendDraft(draftId, payload);
        toast(res.data?.message || "Draft sent");
        this.draftId = null;
        this.lastDraftSavedAt = null;
        await this.fetchEmails();
        return res.data?.data || res.data;
      } catch (e) {
        toast(e?.response?.data?.message || "Failed to send draft", "error");
        throw e;
      } finally {
        this.sending = false;
      }
    },

    // ----------------------------
    // Step 8: attachments
    // ----------------------------
    async uploadAttachments({ files, emailId = null }) {
      this.attachmentsUploading = true;
      try {
        const fd = new FormData();
        if (emailId) fd.append("email_id", emailId);
        [...files].forEach((f) => fd.append("files[]", f));

        const res = await EmailService.uploadAttachments(fd);
        const rows = res.data?.data || [];
        this.attachments = [...this.attachments, ...rows];
        return rows;
      } finally {
        this.attachmentsUploading = false;
      }
    },

    async downloadAttachment(att) {
      const res = await EmailService.downloadAttachment(att.id);
      const blob = new Blob([res.data], {
        type: att.mime_type || "application/octet-stream",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_name || "attachment";
      a.click();
      window.URL.revokeObjectURL(url);
    },

    async getPreviewUrl(att) {
      const res = await EmailService.previewAttachment(att.id);
      return res.data?.url || null;
    },

    async deleteComposeAttachment(attId) {
      await EmailService.deleteAttachment(attId);
      this.attachments = this.attachments.filter((x) => x.id !== attId);
    },
  },
});
